/**
 * Minimal CSV parsing helpers for previewing the FIRST record.
 *
 * Why not split(',')?
 * - CSV allows commas and newlines inside quoted fields, e.g. "hello, world" or "line1\nline2"
 * - Splitting breaks those values and results in truncated previews.
 *
 * This parser is intentionally small and only aims to correctly parse the first few rows.
 */

export const parseCsvRows = (text, maxRows = 2) => {
    if (!text) return [];

    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    // Strip UTF-8 BOM if present
    let i = 0;
    if (text.charCodeAt(0) === 0xFEFF) {
        i = 1;
    }

    for (; i < text.length; i++) {
        const ch = text[i];

        if (ch === '"') {
            // Escaped quote inside a quoted field: ""
            if (inQuotes && text[i + 1] === '"') {
                field += '"';
                i += 1;
                continue;
            }
            inQuotes = !inQuotes;
            continue;
        }

        if (!inQuotes && ch === ',') {
            row.push(field);
            field = '';
            continue;
        }

        // Newline handling (CRLF / LF / CR)
        if (!inQuotes && (ch === '\n' || ch === '\r')) {
            row.push(field);
            field = '';

            // Consume LF after CR (CRLF)
            if (ch === '\r' && text[i + 1] === '\n') {
                i += 1;
            }

            // Only push non-empty rows (but keep header even if empty fields)
            const hasAnyCell = row.some((cell) => cell !== '');
            if (hasAnyCell) {
                rows.push(row);
                if (rows.length >= maxRows) {
                    return rows;
                }
            }
            row = [];
            continue;
        }

        field += ch;
    }

    // Flush trailing field/row
    row.push(field);
    const hasAnyCell = row.some((cell) => cell !== '');
    if (hasAnyCell) {
        rows.push(row);
    }

    return rows.slice(0, maxRows);
};

/**
 * Returns the character index (exclusive) that contains the first `rowCount`
 * complete CSV rows using quote-aware parsing.
 *
 * Example:
 * - rowCount=1 => end of header row
 * - rowCount=2 => end of header + first data row
 *
 * Returns -1 when the requested number of complete rows is not fully present.
 */
export const findCsvEndIndexForRowCount = (text, rowCount = 2) => {
    if (!text || rowCount <= 0) return -1;

    let inQuotes = false;
    let completedRows = 0;
    let i = 0;

    // Strip UTF-8 BOM if present
    if (text.charCodeAt(0) === 0xFEFF) {
        i = 1;
    }

    for (; i < text.length; i++) {
        const ch = text[i];

        if (ch === '"') {
            // Escaped quote inside quoted field: ""
            if (inQuotes && text[i + 1] === '"') {
                i += 1;
                continue;
            }
            inQuotes = !inQuotes;
            continue;
        }

        if (!inQuotes && (ch === '\n' || ch === '\r')) {
            completedRows += 1;
            // consume LF after CR in CRLF
            if (ch === '\r' && text[i + 1] === '\n') {
                i += 1;
            }
            if (completedRows >= rowCount) {
                return i + 1;
            }
        }
    }

    return -1;
};

export const getCsvFieldValueFromFirstRecord = (decodedCsv, fieldName) => {
    if (!decodedCsv || !fieldName) return null;

    const rows = parseCsvRows(decodedCsv, 2);
    if (rows.length < 2) return null;

    const headers = rows[0].map((h) => (h ?? '').trim());
    const firstRow = rows[1];

    const idx = headers.indexOf(fieldName);
    if (idx === -1) return null;
    if (idx >= firstRow.length) return null;

    const value = firstRow[idx];
    if (value === undefined || value === null) return null;

    // Return as-is; caller can decide if it wants trimming.
    return String(value);
};

export const getCsvFieldValuesFromFirstNRecords = (decodedCsv, fieldName, maxRecords = 6) => {
    if (!decodedCsv || !fieldName) return [];
    const n = Math.max(1, Math.min(Number(maxRecords) || 6, 25));

    const rows = parseCsvRows(decodedCsv, n + 1);
    if (rows.length < 2) return [];

    const headers = rows[0].map((h) => (h ?? '').trim());
    const idx = headers.indexOf(fieldName);
    if (idx === -1) return [];

    const values = [];
    for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row || idx >= row.length) continue;
        const v = row[idx];
        if (v === undefined || v === null) continue;
        const s = String(v);
        if (!s.trim()) continue;
        values.push(s);
        if (values.length >= n) break;
    }
    return values;
};


